import { Component, Input, ViewChild, ElementRef } from '@angular/core';
import { BaseEntity, Media } from '@base/entities/BaseEntities';
import { BasePageComponent } from '@base/component/BasePageComponent';
import { ServiceBagService, ServiceBagServices } from '@base/component/ServiceBag';
import { EnumPageDisplayModes, EnumPageEntitySources } from '@core/framework/sysenums.enum';
import { DxTileViewComponent, DxValidationGroupComponent } from 'devextreme-angular';
import { DomSanitizer } from '@angular/platform-browser';
import { Utils } from '@core/helpers/Utils';
import { TemplateRef } from '@angular/core';

class State {
    VideoWidth: number;
    VideoHeight: number;
    PreviewWidth: number;
    PreviewHeight: number;
    VideoAspectRatio: number;

    IsPreview: boolean;
    PreviewMediaItem: Media;

    VideoStream: MediaStream;

    IsVideoMode: boolean = false;
    Recording: Recording = new Recording;
    IsOpened = false;
    IsCameraStarted = false;
}

class Recording {
    IsVideoRecording: boolean = false;
    MediaRecorder: MediaRecorder = null;
    RecordTimeSeconds: number = 0;
    RecordTimeString: string;
    RecordInterval: any;
}

@Component({
    providers: ServiceBagServices,
    selector: 'm4u-photoboxdialog',
    templateUrl: './photoboxdialog.component.html',
    styleUrls: ['./photoboxdialog.component.scss']
})
export class PhotoBoxDialogComponent extends BasePageComponent<BaseEntity, State>
{
    private _mediaItems: Media[] = [];
    private _imageMimeType: string = "image/jpeg";

    @Input() set MediaItems(value) {
        this._mediaItems = value;
        this._mediaItems?.forEach(item => {
            this.SetPreview(item);
        })
    }

    get MediaItems(): Media[] {
        return this._mediaItems;
    }

    @Input() Title: string;
    @Input() IsAutoSavePhotoAfterTake = false;
    @Input() IsAllowVideo: boolean = false;
    @Input() MinCount: number;
    @Input() MaxCount: number;

    @ViewChild("video") public video: ElementRef;
    @ViewChild("canvas") public canvas: ElementRef;
    @ViewChild("preview") public preview: ElementRef;
    @ViewChild("validationGroup") validationGroup: DxValidationGroupComponent;
    @ViewChild(DxTileViewComponent) public thumbs: DxTileViewComponent;
    @Input() ExtraFields: TemplateRef<any>;

    constructor(A: ServiceBagService, private sanitizer: DomSanitizer) {
        super(A, 'PhotoBoxDialogComponent', BaseEntity, State, EnumPageDisplayModes.Dialog, EnumPageEntitySources.External);
    }

    //If custom title was set, return it
    override OnInitTitle(): string {
        if (this.Title == undefined) return undefined;
        return this.GetDefaultTitle() + ' - ' + this.Title;
    }

    override OnInitComponentFinished() {
        setTimeout(() => {
            this.StartVideo();
            this.State.IsOpened = true;
        }, 500);
    }

    override OnDestroyComponent() {
        this.State.IsOpened = false;
        this.State.IsCameraStarted = false;
        this.StopVideo();
    }

    //Stop video and cleanup
    StopVideo() {
        if (this.State.VideoStream != undefined) {
            this.State.PreviewMediaItem = undefined;
            this.State.IsPreview = false;
            this.video.nativeElement.pause();
            this.video.nativeElement.srcObject = undefined;
            this.State.VideoStream.getVideoTracks().forEach(x => x.stop());
            this.State.VideoStream = undefined;
            this.State.IsCameraStarted = false;

        }
    }

    //Plug in video
    StartVideo() {
        //Set size
        this.State.VideoWidth = this.ClientWidth * 3;
        this.State.VideoHeight = (this.ClientHeight - 148) * 3;
        this.State.VideoAspectRatio = this.State.VideoWidth / this.State.VideoHeight;
        this.State.PreviewWidth = this.ClientWidth;
        this.State.PreviewHeight = (this.ClientHeight - 148);


        //Init
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            navigator.mediaDevices.getUserMedia(
                {
                    video:
                    {
                        facingMode: 'environment',
                        width: { ideal: this.State.VideoWidth },
                        height: { ideal: this.State.VideoHeight },
                        aspectRatio: { ideal: this.State.VideoAspectRatio },
                    },
                    audio: false

                }).then(stream => {
                    this.State.IsCameraStarted = true;
                    this.State.VideoStream = stream;
                    this.State.VideoAspectRatio = stream.getVideoTracks()[0].getSettings().aspectRatio;
                    this.State.VideoWidth = stream.getVideoTracks()[0].getSettings().width;
                    this.State.VideoHeight = stream.getVideoTracks()[0].getSettings().height;
                    this.video.nativeElement.srcObject = stream;
                    this.video.nativeElement.play();
                }, error => {
                    console.error(error);
                    this.UI.StopMessage(this.RC('msgCouldNotStartVideoError'));
                });
        }
    }


    //Build new structure, and add to array
    //Give identifiers starting with -1 and going down
    OnTakePhoto() {
        let photo = this.TakePhoto();
        this.MediaItems.push(photo);
        if (this.IsAutoSavePhotoAfterTake) { this.OnSavePhotos(); }
    }

    OnStartVideoRecording() {
        let photo = this.TakePhoto();

        this.State.Recording.RecordInterval = setInterval(() => {
            this.State.Recording.RecordTimeSeconds++;
            this.State.Recording.RecordTimeString = new Date(this.State.Recording.RecordTimeSeconds * 1000).toISOString().slice(14, 19);
        }, 1000);

        this.StartRecording().then((recordedChunks) => {
            let recordedBlob = this.GetVideoBlob(recordedChunks);
            photo.VideoDataUrl = this.GetSafeUrlFromBlob(recordedBlob);
            Utils.ConvertBlobToBytesArray(recordedBlob, (array) => {
                photo.VideoBytes = array;
            });
            this.MediaItems.push(photo);
        }).catch((error) => {
            console.error(error);
            this.UI.StopMessage(`${error?.error} ${error?.message}`);
        });
    }

    OnStopVideoRecording() {
        if (this.State.Recording.MediaRecorder?.state === "recording") {
            this.State.Recording.MediaRecorder.stop();
            this.State.Recording.IsVideoRecording = false;
        }

        if (this.State.Recording.RecordInterval) {
            clearInterval(this.State.Recording.RecordInterval);
            this.State.Recording.RecordInterval = null;
            this.State.Recording.RecordTimeString = null;
            this.State.Recording.RecordTimeSeconds = 0;
        }
    }

    OnResizeWindow() {
        if (!this.IsDialogVisible) return;
        this.StartVideo();
    }

    OnOrientationChanged() {
        if (!this.IsDialogVisible) return;
        this.StartVideo();
    }


    //Show photo in the preview box
    OnShowMediaItem(item: Media) {
        this.State.PreviewMediaItem = item;
        this.State.IsPreview = true;
        this.DetectChanges();
        this.preview.nativeElement.src = item.ImageBase64;
    }

    //Switch back to video
    OnClosePreview() {
        this.State.PreviewMediaItem = undefined;
        this.State.IsPreview = false;
        this.StartVideo();
    }


    //Delete from photo list
    OnRemovePhoto() {
        this.MediaItems = this.MediaItems.filter(x => x.EntryID != this.State.PreviewMediaItem.EntryID);
        this.thumbs.instance.repaint()
        this.OnClosePreview();
    }

    //Finish - emit the PhotoChanged event to update parent state, and close
    OnSavePhotos() {
        this.ValidateValidationGroup(this.validationGroup).then(isValid => {
            if (!isValid) return;

            if (this.MinCount && this.MediaItems.length < this.MinCount) {
                this.UI.StopMessage(this.RC('msgPhotoMinCountError', this.MinCount));
                return;
            }

            if (this.MaxCount && this.MediaItems.length > this.MaxCount) {
                this.UI.StopMessage(this.RC('msgPhotoMaxCountError', this.MaxCount));
                return;
            }

            this.StopVideo();
            this.CloseDialogWithSuccess(this.MediaItems);
            this.ClosePage();
        });
    }

    OnSetVideoMode() {
        this.State.IsVideoMode = true;
    }

    OnSetPhotoMode() {
        this.State.IsVideoMode = false;
    }

    OnFileSelected(e) {
        const files = e?.target?.files;
        if (!files || files?.length == 0) return;

        const file = files[0];
        const isImage = file.type.startsWith("image");
        const fileReader = new FileReader();

        if (isImage) {
            fileReader.addEventListener("load", (event) => {
                const photo = this.AddPhoto(event.target.result);
                this.MediaItems.push(photo);
            });

            fileReader.readAsDataURL(files[0]);
        }
        else {
            fileReader.addEventListener('load', async () => {
                const arrayBuffer = (fileReader.result as ArrayBuffer);
                const result = Array.from(new Uint8Array(arrayBuffer));
                const blob = new Blob([new Uint8Array(result)], { type: file.type });

                const media = new Media;
                media.EntryID = this.GetNewMediaItemEntryID();
                const imagePreviewBlob = await this.GetVideoCover(blob);

                Utils.ConvertBlobToBytesArray(imagePreviewBlob, (array) => {
                    media.ImageBytes = array;
                    this.SetPreview(media);
                });

                media.VideoBytes = result;
                this.SetPreview(media);
                this.MediaItems.push(media);
            }, false);

            fileReader.readAsArrayBuffer(file);
        }

        const imageGallery = document.getElementById('image-gallery') as any;
        imageGallery && (imageGallery.value = '');
    }

    private GetNewMediaItemEntryID() {
        return this.MediaItems.reduce((min, p) => p.EntryID < min ? p.EntryID : min, 0) - 1;
    }

    private TakePhoto(): Media {
        this.canvas.nativeElement.getContext("2d").drawImage(this.video.nativeElement, 0, 0, this.State.VideoWidth, this.State.VideoHeight);
        return this.AddPhoto(this.canvas.nativeElement.toDataURL(this._imageMimeType, 1));
    }

    private AddPhoto(base64) {
        const photo = new Media;
        photo.ImageBase64 = base64;
        photo.EntryID = this.GetNewMediaItemEntryID();
        photo.ImageBytes = Utils.ConvertToBytesArray(photo.ImageBase64);
        return photo;
    }

    private StartRecording() {
        this.State.Recording.MediaRecorder = new MediaRecorder(this.State.VideoStream);
        let data = [];

        this.State.Recording.MediaRecorder.ondataavailable = (event) => data.push(event.data);
        this.State.Recording.MediaRecorder.start();
        this.State.Recording.IsVideoRecording = true;

        let stopped = new Promise((resolve, reject) => {
            this.State.Recording.MediaRecorder.onstop = resolve;
            this.State.Recording.MediaRecorder.onerror = (event) => reject(event);
        });

        return stopped.then(() => data);
    }

    private SetPreview(item: Media) {
        if (item.VideoBytes?.length > 0 && !item.VideoDataUrl) item.VideoDataUrl = this.GetSafeUrlFromBlob(this.GetVideoBlob([new Uint8Array(item.VideoBytes)]));
        if (item.ImageBytes?.length > 0 && !item.ImageBase64) item.ImageBase64 = Utils.GetImageSource(item.ImageBytes, this._imageMimeType);
    }

    private GetVideoBlob(data): Blob {
        return new Blob(data, { type: "video/webm" });
    }

    private GetSafeUrlFromBlob(data: Blob) {
        return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(data));
    }

    private GetVideoCover(data: Blob, seekTo = 0.0) {
        return new Promise<Blob>((resolve, reject) => {
            try {
                // load the file to a video player
                const videoPlayer = document.createElement('video');
                videoPlayer.setAttribute('src', URL.createObjectURL(data));
                videoPlayer.load();
                videoPlayer.addEventListener('error', (ex) => {
                    reject(ex);
                });
                // load metadata of the video to get video duration and dimensions
                videoPlayer.addEventListener('loadedmetadata', () => {
                    // seek to user defined timestamp (in seconds) if possible
                    if (videoPlayer.duration < seekTo) {
                        reject("video is too short.");
                        return;
                    }
                    // delay seeking or else 'seeked' event won't fire on Safari
                    setTimeout(() => {
                        videoPlayer.currentTime = seekTo;
                    }, 200);
                    // extract video thumbnail once seeking is complete
                    videoPlayer.addEventListener('seeked', () => {
                        // define a canvas to have the same dimension as the video
                        const canvas = document.createElement("canvas");
                        canvas.width = videoPlayer.videoWidth;
                        canvas.height = videoPlayer.videoHeight;
                        // draw the video frame to canvas
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(videoPlayer, 0, 0, canvas.width, canvas.height);
                        // return the canvas image as a blob
                        ctx.canvas.toBlob(
                            blob => {
                                resolve(blob);
                            },
                            "image/jpeg",
                            0.75 /* quality */
                        );
                    });
                });
            }
            catch (ex) {
                console.error(ex);
            }
        });
    }
}
